import React from 'react'

/**
 * Which items a case refund covers (spec docs/specs_20261008_item_refunds.md).
 *
 * Refund all, or Select items with a tick box per order line, plus a shipping
 * refund that starts at £0.00. The total adds up as staff tick. Lines already
 * refunded show "Refunded" and cannot be ticked. When the order's lines cannot
 * be refunded one by one (items_available false) the whole order is refunded.
 *
 * `info` is the refund-info response. The parent owns mode, refs and shipping
 * so the Process full refund button and the partial modal share one selection.
 */
export default function RefundItemsPicker({ info, mode, setMode, refs, setRefs, shipping, setShipping, idPrefix, hideShipping = false }) {
  if (!info) return <div style={{ fontSize: 12, color: 'var(--color-text-muted)', textAlign: 'center', marginBottom: 10 }}>Loading order items...</div>

  const lines = info.line_items || []
  const shippingCap = Number(info.shipping_refundable || 0)
  const total = refundSelectionTotal(info, mode, refs, hideShipping ? 0 : shipping)
  const toggle = ref => setRefs(prev => prev.includes(ref) ? prev.filter(r => r !== ref) : [...prev, ref])

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, marginBottom: 10, textAlign: 'left' }}>
      {info.items_available ? (
        <>
          <div role="radiogroup" aria-label="What to refund" style={{ display: 'flex', gap: 8, padding: 8 }}>
            {[['all', 'Refund all'], ['items', 'Select items']].map(([value, label]) => (
              <label key={value} style={{
                flex: 1, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', fontSize: 12, cursor: 'pointer',
                border: `1px solid ${mode === value ? '#2563eb' : '#e5e7eb'}`, borderRadius: 6,
                background: mode === value ? '#eff6ff' : '#fff'
              }}>
                <input type="radio" id={`${idPrefix}-mode-${value}`} name={`${idPrefix}-mode`} value={value}
                  checked={mode === value} onChange={() => setMode(value)} />
                {label}
              </label>
            ))}
          </div>
          {lines.map(line => {
            const locked = mode === 'all' || !line.selectable
            const checked = line.selectable && (mode === 'all' || refs.includes(line.ref))
            return (
              <label key={line.ref} style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 8, alignItems: 'center',
                padding: '8px', borderTop: '1px solid #f3f4f6', fontSize: 12,
                cursor: locked ? 'default' : 'pointer', color: line.selectable ? 'inherit' : '#9ca3af'
              }}>
                <input type="checkbox" id={`${idPrefix}-item-${line.ref}`} checked={checked} disabled={locked}
                  onChange={() => toggle(line.ref)} />
                <span style={{ minWidth: 0 }}>
                  {line.title}
                  {line.quantity > 1 && <span style={{ color: '#6b7280' }}> × {line.quantity}</span>}
                  {line.ticked && line.selectable && <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>On this case</span>}
                </span>
                <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: line.selectable ? 600 : 400 }}>
                  {line.selectable ? `£${Number(line.refundable).toFixed(2)}` : 'Refunded'}
                </span>
              </label>
            )
          })}
        </>
      ) : (
        <div style={{ padding: 8, fontSize: 12, color: '#6b7280' }}>
          This order's items can't be refunded one by one, so the whole order is refunded.
        </div>
      )}
      {!hideShipping && <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center', padding: 8, borderTop: '1px solid #f3f4f6', fontSize: 12 }}>
        <label htmlFor={`${idPrefix}-shipping`}>
          Shipping refund
          <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
            {shippingCap > 0 ? `Up to £${shippingCap.toFixed(2)}` : 'No shipping left to refund'}
          </span>
        </label>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, border: '1px solid #d1d5db', borderRadius: 4, padding: '2px 6px' }}>
          £<input id={`${idPrefix}-shipping`} inputMode="decimal" value={shipping} disabled={shippingCap <= 0}
            onChange={e => setShipping(e.target.value)}
            onBlur={() => setShipping(clampShipping(shipping, shippingCap).toFixed(2))}
            style={{ width: 56, border: 0, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600, background: 'transparent' }} />
        </span>
      </div>}
      {info.items_available && (
        <div style={{ display: 'flex', justifyContent: 'space-between', padding: 8, background: '#111827', color: '#fff', fontWeight: 600, fontSize: 13, fontVariantNumeric: 'tabular-nums', borderRadius: '0 0 6px 6px' }}>
          <span>Total</span><span>£{total.toFixed(2)}</span>
        </div>
      )}
    </div>
  )
}

/** The ticked items' refundable value plus the shipping refund, in pounds. */
export function refundSelectionTotal(info, mode, refs, shipping) {
  const lines = (info?.line_items || []).filter(l => l.selectable && (mode === 'all' || refs.includes(l.ref)))
  const items = lines.reduce((sum, l) => sum + Number(l.refundable || 0), 0)
  return Math.round((items + clampShipping(shipping, Number(info?.shipping_refundable || 0))) * 100) / 100
}

/** The request body selection for the chosen mode. */
export function refundSelection(info, mode, refs) {
  if (!info?.items_available || mode === 'all') return { mode: 'all' }
  return { mode: 'items', refs }
}

/** Titles of the lines a selection covers, for confirm text and the partial modal. */
export function refundSelectionTitles(info, mode, refs) {
  if (!info?.items_available) return []
  return (info.line_items || [])
    .filter(l => l.selectable && (mode === 'all' || refs.includes(l.ref)))
    .map(l => l.title)
}

/** Where the selection starts: the case's own items when it has any, else Refund all. */
export function initialRefundSelection(info) {
  const ticked = (info?.line_items || []).filter(l => l.ticked && l.selectable).map(l => l.ref)
  return info?.items_available && ticked.length ? { mode: 'items', refs: ticked } : { mode: 'all', refs: [] }
}

function clampShipping(value, cap) {
  const n = parseFloat(value)
  if (!Number.isFinite(n) || n < 0) return 0
  return Math.min(n, cap)
}
